// Phase 1 configs — only compression, target-size, and use-case/optimization clusters

export interface TargetSizeConfig {
  size: string;
  slug: string;
  keyword: 'imagen' | 'foto';
}

export interface CompressionConfig {
  keyword: string;
  displayKeyword: string;
  slug: string;
}

export interface UseCaseConfig {
  useCase: string;
  context: string;
  slug: string;
  detailText: string;
  pageType: 'compression' | 'optimization';
}

// ─── Target-size: new sizes not already in manual tools ─────────────────────
export const phase1TargetSizeConfigs: TargetSizeConfig[] = [
  { size: '30KB', slug: 'reducir-imagen-30kb', keyword: 'imagen' },
  { size: '75KB', slug: 'reducir-imagen-75kb', keyword: 'imagen' },
  { size: '1MB', slug: 'reducir-imagen-1mb', keyword: 'imagen' },
  { size: '3MB', slug: 'reducir-imagen-3mb', keyword: 'imagen' },
  { size: '50KB', slug: 'reducir-foto-50kb', keyword: 'foto' },
  { size: '300KB', slug: 'reducir-foto-300kb', keyword: 'foto' },
  { size: '500KB', slug: 'reducir-foto-500kb', keyword: 'foto' },
];

// ─── Compression synonyms ───────────────────────────────────────────────────
export const phase1CompressionConfigs: CompressionConfig[] = [
  { keyword: 'fotos-online', displayKeyword: 'comprimir fotos online', slug: 'comprimir-fotos-online' },
  { keyword: 'imagen-gratis', displayKeyword: 'comprimir imagen gratis', slug: 'comprimir-imagen-gratis' },
  { keyword: 'reducir-peso', displayKeyword: 'reducir peso de imagen', slug: 'reducir-peso-imagen' },
  { keyword: 'foto-online', displayKeyword: 'comprimir foto online', slug: 'comprimir-foto-online' },
  { keyword: 'imagen-jpg', displayKeyword: 'comprimir imagen JPG', slug: 'comprimir-imagen-jpg' },
  { keyword: 'reducir-tamano', displayKeyword: 'reducir tamaño de imagen', slug: 'reducir-tamano-imagen' },
  { keyword: 'reducir-peso-foto', displayKeyword: 'reducir peso de foto', slug: 'reducir-peso-foto' },
];

// ─── Use-case / optimization pages ──────────────────────────────────────────
export const phase1UseCaseConfigs: UseCaseConfig[] = [
  {
    useCase: 'comprimir-whatsapp',
    context: 'WhatsApp',
    slug: 'comprimir-imagen-para-whatsapp',
    detailText: 'WhatsApp comprime automáticamente las imágenes que envías, reduciendo su calidad. Si comprimes tus fotos antes de enviarlas, puedes controlar la compresión y mantener una calidad superior.',
    pageType: 'compression',
  },
  {
    useCase: 'comprimir-cv',
    context: 'currículum vitae',
    slug: 'comprimir-imagen-para-cv',
    detailText: 'Los portales de empleo y sistemas de selección suelen limitar el tamaño de las fotos en los CV a 100KB-500KB. Comprime tu foto profesional para que cumpla con estos requisitos sin perder calidad.',
    pageType: 'compression',
  },
  {
    useCase: 'comprimir-web-gratis',
    context: 'web (gratis)',
    slug: 'comprimir-imagen-para-web-gratis',
    detailText: 'Las imágenes sin optimizar son la causa principal de sitios web lentos. Comprime tus fotos gratis para mejorar la velocidad de carga, el SEO y la experiencia del usuario.',
    pageType: 'optimization',
  },
  {
    useCase: 'optimizar-seo',
    context: 'SEO',
    slug: 'optimizar-imagen-seo',
    detailText: 'Google penaliza los sitios con imágenes pesadas. Optimiza tus imágenes para mejorar las métricas Core Web Vitals, reducir el LCP y posicionar mejor en los resultados de búsqueda.',
    pageType: 'optimization',
  },
  {
    useCase: 'optimizar-blog',
    context: 'blog',
    slug: 'optimizar-imagen-para-blog',
    detailText: 'Las imágenes de tu blog deben cargarse rápidamente para mantener a los lectores. Optimiza cada imagen antes de publicarla para lograr tiempos de carga mínimos y mejor posicionamiento orgánico.',
    pageType: 'optimization',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// Phase 2 configs — crop, rotate, resize-percent, batch
// ═══════════════════════════════════════════════════════════════════════════

export interface CropConfig {
  slug: string;
  aspectRatio?: string;
  platform?: string;
}

export interface RotateConfig {
  slug: string;
  keyword: string;
  displayKeyword: string;
  rotatePreset?: string;
}

export interface ResizePercentConfig {
  slug: string;
  percent: number;
  keyword: string;
  displayKeyword: string;
}

export interface BatchConfig {
  slug: string;
  batchToolType: string;
  keyword: string;
  displayKeyword: string;
}

export const phase2CropConfigs: CropConfig[] = [
  { slug: 'recortar-imagen' },
  { slug: 'recortar-imagen-online' },
  { slug: 'recortar-imagen-instagram', aspectRatio: '1:1', platform: 'Instagram' },
  { slug: 'recortar-imagen-cuadrada', aspectRatio: '1:1' },
  { slug: 'recortar-imagen-16-9', aspectRatio: '16:9' },
];

export const phase2RotateConfigs: RotateConfig[] = [
  { slug: 'rotar-imagen', keyword: 'rotar', displayKeyword: 'rotar imagen' },
  { slug: 'girar-foto', keyword: 'girar', displayKeyword: 'girar foto' },
  { slug: 'voltear-imagen', keyword: 'voltear', displayKeyword: 'voltear imagen', rotatePreset: 'flip-h' },
  { slug: 'rotar-imagen-90-grados', keyword: 'rotar-90', displayKeyword: 'rotar imagen 90 grados', rotatePreset: '90' },
];

export const phase2ResizePercentConfigs: ResizePercentConfig[] = [
  { slug: 'redimensionar-imagen-50-porciento', percent: 50, keyword: 'reducir', displayKeyword: 'reducir imagen al 50%' },
  { slug: 'redimensionar-imagen-25-porciento', percent: 25, keyword: 'reducir', displayKeyword: 'reducir imagen al 25%' },
  { slug: 'aumentar-tamano-imagen', percent: 150, keyword: 'aumentar', displayKeyword: 'aumentar tamaño de imagen' },
];

export const phase2BatchConfigs: BatchConfig[] = [
  { slug: 'comprimir-varias-imagenes', batchToolType: 'compression', keyword: 'comprimir-varias', displayKeyword: 'comprimir varias imágenes' },
];

// ═══════════════════════════════════════════════════════════════════════════
// Phase 3 configs — adjust, image-to-pdf, advanced conversion (AVIF)
// ═══════════════════════════════════════════════════════════════════════════

export interface AdjustConfig {
  slug: string;
  preset: string;
  displayKeyword: string;
}

export interface PdfConfig {
  slug: string;
  displayKeyword: string;
}

export interface AdvancedConvertConfig {
  slug: string;
  sourceFormat: string;
  targetFormat: string;
}

export const phase3AdjustConfigs: AdjustConfig[] = [
  { slug: 'ajustar-brillo-imagen', preset: 'brightness', displayKeyword: 'ajustar brillo de imagen' },
  { slug: 'ajustar-contraste-imagen', preset: 'contrast', displayKeyword: 'ajustar contraste de imagen' },
  { slug: 'mejorar-calidad-imagen', preset: 'improve', displayKeyword: 'mejorar calidad de imagen' },
  { slug: 'enfocar-imagen', preset: 'sharpen', displayKeyword: 'enfocar imagen' },
  { slug: 'suavizar-imagen', preset: 'blur', displayKeyword: 'suavizar imagen' },
];

export const phase3PdfConfigs: PdfConfig[] = [
  { slug: 'convertir-imagen-pdf', displayKeyword: 'convertir imagen a PDF' },
  { slug: 'imagenes-a-pdf', displayKeyword: 'imágenes a PDF' },
  { slug: 'jpg-a-pdf', displayKeyword: 'JPG a PDF' },
  { slug: 'png-a-pdf', displayKeyword: 'PNG a PDF' },
];

export const phase3AdvancedConvertConfigs: AdvancedConvertConfig[] = [
  { slug: 'convertir-avif-jpg', sourceFormat: 'AVIF', targetFormat: 'JPG' },
  { slug: 'convertir-avif-png', sourceFormat: 'AVIF', targetFormat: 'PNG' },
];

// ═══════════════════════════════════════════════════════════════════════════
// Phase 4 configs — target-size use-case, adjust intents, resize use-case
// ═══════════════════════════════════════════════════════════════════════════

export interface Phase4TargetUseCaseConfig {
  slug: string;
  size: string;
  context: string;
  detailText: string;
}

export const phase4TargetUseCaseConfigs: Phase4TargetUseCaseConfig[] = [
  {
    slug: 'reducir-imagen-para-cv',
    size: '200KB',
    context: 'currículum vitae',
    detailText: 'Muchos portales de empleo exigen fotos de menos de 200KB. Con esta herramienta puedes reducir tu foto profesional al peso exacto que requiere la plataforma, sin perder calidad visible.',
  },
];

export const phase4AdjustConfigs: AdjustConfig[] = [
  { slug: 'quitar-ruido-imagen', preset: 'blur', displayKeyword: 'quitar ruido de imagen' },
  { slug: 'eliminar-grano-imagen', preset: 'blur', displayKeyword: 'eliminar grano de imagen' },
  { slug: 'mejorar-foto-borrosa', preset: 'sharpen-improve', displayKeyword: 'mejorar foto borrosa' },
  { slug: 'aclarar-imagen-oscura', preset: 'brighten-contrast', displayKeyword: 'aclarar imagen oscura' },
  { slug: 'mejorar-calidad-foto-online', preset: 'improve', displayKeyword: 'mejorar calidad de foto online' },
];

export interface Phase4ResizeConfig {
  slug: string;
  platform: string;
  dimensions: string;
  displayKeyword: string;
}

export const phase4ResizeConfigs: Phase4ResizeConfig[] = [
  { slug: 'redimensionar-imagen-para-whatsapp', platform: 'WhatsApp', dimensions: '1080x1920', displayKeyword: 'redimensionar imagen para WhatsApp' },
  { slug: 'redimensionar-imagen-para-cv', platform: 'CV', dimensions: '600x600', displayKeyword: 'redimensionar imagen para CV' },
];
